<template>
  <div>
    <b-form-select
      v-model="selectedEvaluationId"
      size="sm"
      class="select"
      :disabled="!allows_crud"
    >
      <b-form-select-option :value="null">{{
        $t("teaching.sessionEvaluationLink.noSelection")
      }}</b-form-select-option>
      <b-form-select-option
        v-for="evaluation in selectableOptions"
        :key="evaluation.id"
        :value="evaluation.id"
        >{{ evaluation.name ? evaluation.name : "---" }}</b-form-select-option
      >
    </b-form-select>
    <template v-if="selectedEvaluation">
      <template v-if="selectedEvaluation.weighing">
        <div class="col colored-div">
          {{ $t("teaching.sessionEvaluationLink.weighing") }}
        </div>
        <div class="col">{{ selectedEvaluation.weighing }}%</div>
      </template>
      <template v-if="evaluationTypeLabel">
        <div class="col colored-div">
          {{ $t("teaching.sessionEvaluationLink.type") }}
        </div>
        <div class="col">{{ evaluationTypeLabel }}</div>
      </template>
      <template v-if="evaluationCriteriaMacroList.length > 0">
        <div class="col colored-div">
          {{
            $getVisibleNames(
              "teaching.evaluationcriteriamacro",
              true,
              "Criterios de Evaluación Macro"
            )
          }}
        </div>
        <ul class="col">
          <li
            v-for="criteria in evaluationCriteriaMacroList"
            :key="`session-evaluation-link-macro-${criteria.id}`"
          >
            <SentenceContainer :Sentence="criteria"></SentenceContainer>
          </li>
        </ul>
      </template>
      <template v-if="evaluationCriteriaMicroList.length > 0">
        <div class="col colored-div">
          {{
            $getVisibleNames(
              "teaching.evaluationcriteriamicro",
              true,
              "Criterios de Evaluación Micro"
            )
          }}
        </div>
        <div class="col ec-badge-container">
          <template v-for="group in groupedEvaluationCriteriaMicro">
            <b-badge
              v-for="criteria in group.options"
              :key="`session-evaluation-link-micro-${criteria.id}`"
              class="ec-micro-badge"
              v-b-tooltip.v-secondary.noninteractive="criteria.full_sentence"
              >{{ group.order }}.{{ criteria.order }}</b-badge
            >
          </template>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";

export default {
  name: "SessionEvaluationLink",
  components: {
    SentenceContainer: () => import("@/components/reusable/SentenceContainer"),
  },
  props: {
    SessionPlanning: {
      type: Object,
      required: true,
    },
    available_evaluations: {
      type: Array,
      required: true,
    },
    field_name: {
      type: String,
      required: true,
    },
    sibling_session_plannings: {
      type: Array,
      default: () => [],
    },
    EvaluationCriteriaContents: {
      type: Array,
      default: null,
    },
    allows_crud: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      session_planning: this.SessionPlanning,
    };
  },
  computed: {
    ...mapGetters({
      epmEvaluationTypes: names.EPM_EVALUATION_TYPES,
      evaluationCriteriasMacros: names.EVALUATION_CRITERIAS_MACROS,
      evaluationCriterias: names.EVALUATION_CRITERIAS,
      studyUnits: names.STUDY_UNITS,
      studyUnitcontents: names.CONTENTS,
      institution: "getInstitution",
    }),
    selectedEvaluationId: {
      get() {
        return this.session_planning[this.field_name];
      },
      set(value) {
        const evaluation =
          value != null
            ? this.available_evaluations.find((x) => x.id == value)
            : null;
        if (!evaluation) {
          this.patchSessionPlanning({ [this.field_name]: value });
          return;
        }
        const merged_item = this.mergedSelectionsFrom(evaluation);
        const could_merge_macro =
          this.institution &&
          this.institution.show_evaluation_criteria_macro &&
          evaluation.evaluation_criterias_macro.length > 0;
        if (Object.keys(merged_item).length == 0 && !could_merge_macro) {
          this.patchSessionPlanning({ [this.field_name]: value });
          return;
        }
        // El merge solo se aplica si el usuario lo confirma; si lo rechaza,
        // igual se guarda el vínculo con la evaluación, sin combinar nada.
        this.$swal({
          title: this.$t("teaching.sessionEvaluationLink.mergeConfirmTitle"),
          text: this.mergeConfirmText,
          type: "question",
          showCancelButton: true,
        }).then((result) => {
          const item = { [this.field_name]: value };
          if (result.value) Object.assign(item, merged_item);
          this.patchSessionPlanning(item, result.value ? evaluation : null);
        });
      },
    },
    selectedEvaluation() {
      if (!this.selectedEvaluationId) return null;
      return this.available_evaluations.find(
        (x) => x.id == this.selectedEvaluationId
      );
    },
    // Texto del confirm de merge; las entidades usan nombres alternativos
    // ($getVisibleNames), por eso se interpola aquí y no en el locale.
    mergeConfirmText() {
      return this.$t("teaching.sessionEvaluationLink.mergeConfirmText", {
        achievementIndicators: this.$getVisibleNames(
          "manual.indicador_de_logro",
          true,
          "Indicadores de Logro"
        ),
        methodologicalStrategies: this.$getVisibleNames(
          "mesh.methodologicalstrategy",
          true,
          "Estrategias Metodológicas"
        ),
        autonomousActivities: this.$getVisibleNames(
          "mesh.autonomousteachingactivity",
          true,
          "Actividades de Trabajo Autónomo"
        ),
      });
    },
    selectableOptions() {
      const taken_ids = this.sibling_session_plannings
        .filter((x) => x.id != this.session_planning.id)
        .map((x) => x[this.field_name])
        .filter((id) => id != null);
      return this.available_evaluations.filter(
        (x) => !taken_ids.includes(x.id)
      );
    },
    evaluationTypeLabel() {
      if (!this.selectedEvaluation) return null;
      const type = this.epmEvaluationTypes.find(
        (x) => x.id == this.selectedEvaluation.type
      );
      return type ? type.value : null;
    },
    evaluationCriteriaMacroList() {
      if (!this.selectedEvaluation) return [];
      return this.evaluationCriteriasMacros.filter((x) =>
        this.selectedEvaluation.evaluation_criterias_macro.includes(x.id)
      );
    },
    evaluationCriteriaMicroList() {
      if (!this.selectedEvaluation) return [];
      return this.evaluationCriterias.filter((x) =>
        this.selectedEvaluation.evaluation_criterias_micro.includes(x.id)
      );
    },
    groupedEvaluationCriteriaMicro() {
      let list = [];
      this.evaluationCriteriaMicroList.forEach((criteria) => {
        let group = list.find((x) => x.id == criteria.study_unit);
        if (!group) {
          const study_unit = this.studyUnits.find(
            (x) => x.id == criteria.study_unit
          );
          group = {
            id: criteria.study_unit,
            name: study_unit ? study_unit.name : "",
            order: study_unit ? study_unit.order : 0,
            options: [],
          };
          list.push(group);
        }
        group.options.push(criteria);
      });
      list.sort((a, b) => a.order - b.order);
      list.forEach((group) => {
        group.options.sort((a, b) => a.order - b.order);
      });
      return list;
    },
  },
  methods: {
    // Merge (no reemplazo): agrega lo que trae la evaluación sin quitar
    // selecciones manuales previas de la sesión. Solo incluye en el item los
    // campos donde efectivamente se agrega algo.
    mergedSelectionsFrom(evaluation) {
      const item = {};
      const mergeIntoItem = (field, added_ids) => {
        const current = this.session_planning[field] || [];
        const merged = [...new Set([...current, ...added_ids])];
        if (merged.length > current.length) item[field] = merged;
      };
      mergeIntoItem(
        "evaluation_criterias_micro",
        evaluation.evaluation_criterias_micro
      );
      mergeIntoItem(
        "autonomous_teaching_activities",
        evaluation.autonomous_activities || []
      );
      mergeIntoItem(
        "bibliographic_resources",
        evaluation.bibliographic_resources || []
      );
      // La evaluación no tiene contenidos ni estrategias metodológicas propios:
      // se derivan de las unidades de estudio cubiertas por sus IL micro.
      const study_unit_ids = [
        ...new Set(
          this.evaluationCriterias
            .filter((x) => evaluation.evaluation_criterias_micro.includes(x.id))
            .map((x) => x.study_unit)
        ),
      ];
      // Sin Contenidos por RA Micro (p. ej. PUCV) no se copian: la columna
      // está oculta en la planificación.
      if (!this.$hidesStudyUnitContents(this.institution))
        mergeIntoItem(
          "contents",
          this.studyUnitcontents
            .filter((x) => study_unit_ids.includes(x.study_unit))
            .map((x) => x.id)
        );
      const covered_study_units = this.studyUnits.filter((x) =>
        study_unit_ids.includes(x.id)
      );
      mergeIntoItem(
        "methodological_strategies",
        [].concat(
          ...covered_study_units.map((x) => x.methodological_strategies || [])
        )
      );
      if (
        this.institution &&
        this.institution.show_evaluation_criteria_macro &&
        this.EvaluationCriteriaContents
      ) {
        mergeIntoItem(
          "evaluation_criteria_macro_contents",
          this.EvaluationCriteriaContents.filter((x) =>
            evaluation.evaluation_criterias_macro.includes(
              x.evaluation_criteria_macro
            )
          ).map((x) => x.id)
        );
      }
      return item;
    },
    patchSessionPlanning(item, evaluation = null) {
      const merged_selections = Object.keys(item).length > 1;
      const payload = {
        session_planning_id: this.session_planning.id,
        item: item,
      };
      this.$store.dispatch(names.PATCH_SESSION_PLANNING, payload).then((response) => {
        this.session_planning = response;
        this.$emit("updated_session_planning", response);
        if (evaluation) {
          // Los criterios macro viven en el registro puente (matter/ep_matter/
          // section-session-planning); el merge de esos lo hace SessionRow.
          this.$emit("evaluation_linked", evaluation);
          if (merged_selections)
            toast(this.$t("teaching.sessionEvaluationLink.selectionsMerged"));
        }
      });
    },
  },
  created() {
    this.$store.dispatch(names.FETCH_EPM_EVALUATION_TYPES);
  },
  watch: {
    SessionPlanning() {
      this.session_planning = this.SessionPlanning;
    },
  },
};
</script>

<style scoped>
.select {
  font-size: var(--kl-fs-caption);
}
.colored-div {
  /* Etiqueta de sección dentro de la celda: chip suave del estándar. */
  background-color: var(--kl-chip-bg);
  color: var(--kl-chip-text);
  margin: 0;
  padding: 0;
}
.col {
  padding: 0px;
}
.ec-badge-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}
.ec-micro-badge {
  /* Indicador micro: chip suave del estándar (píldora, 12px, semibold). */
  font-size: var(--kl-chip-fs);
  font-weight: var(--kl-chip-fw);
  background-color: var(--kl-chip-bg);
  color: var(--kl-chip-text);
  border: 1px solid var(--kl-chip-border);
  border-radius: var(--kl-chip-radius);
  text-align: center;
  width: fit-content !important;
  margin-right: 0.3em;
  margin-top: 1px;
  margin-bottom: 1px;
}
</style>
